import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '../components/Icon'
import { languageFromPath, withLang } from '../i18n/i18n'
import { useDocumentMeta } from '../i18n/useDocumentMeta'

// "How it works" page. The animated landmark hero that used to lead this
// page now lives on the landing page (see HeroScene). This page starts
// directly with the "How to set up your eSIM" 3-step overview, then the
// Step 1/2/3 deep-dives and a final CTA.

type ActivationMethod = 'onetap' | 'qr' | 'manual'

// Card illustration mapping per activation method. Static — only the
// text changes per language, which is pulled from how.json at render.
const ACTIVATION_ILLUST: Record<ActivationMethod, ActivationIllustVariant[]> = {
  onetap: ['envelope', 'install-btn', 'confirm'],
  qr: ['laptop-qr', 'settings', 'scan'],
  manual: ['email-keys', 'settings', 'paste'],
}

export default function HowItWorks() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation('how')
  const lang = languageFromPath(location.pathname)
  useDocumentMeta({ title: t('seo.title'), description: t('seo.description') })
  const [activationMethod, setActivationMethod] = useState<ActivationMethod>('onetap')

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Build activation card data from JSON keys + static illustration mapping.
  const activeIllusts = ACTIVATION_ILLUST[activationMethod]
  const activeSubtitle = t(`activation.${activationMethod}.subtitle`)
  const activeCards = [
    {
      illust: activeIllusts[0],
      title: t(`activation.${activationMethod}.card1Title`),
      body: t(`activation.${activationMethod}.card1Body`),
    },
    {
      illust: activeIllusts[1],
      title: t(`activation.${activationMethod}.card2Title`),
      body: t(`activation.${activationMethod}.card2Body`),
    },
    {
      illust: activeIllusts[2],
      title: t(`activation.${activationMethod}.card3Title`),
      body: t(`activation.${activationMethod}.card3Body`),
    },
  ]

  return (
    <>
      {/* ============= 3 SETUP STEPS (page now starts here) ============= */}
      <section className="section how-steps how-steps--top">
        <div className="section-inner">
          <div className="how-steps-head">
            <div className="eyebrow" style={{ marginBottom: 12 }}>{t('setupOverview.eyebrow')}</div>
            <h2 className="h1">{t('setupOverview.title')}</h2>
          </div>
          <div className="how-step-grid">
            <div className="how-step-card">
              <div className="how-step-num n1">01</div>
              <h3>{t('setupOverview.step1.title')}</h3>
              <p>{t('setupOverview.step1.body')}</p>
              <button className="link" onClick={() => scrollTo('step1')}>{t('setupOverview.step1.link')}</button>
            </div>
            <div className="how-step-card">
              <div className="how-step-num n2">02</div>
              <h3>{t('setupOverview.step2.title')}</h3>
              <p>{t('setupOverview.step2.body')}</p>
              <button className="link" onClick={() => scrollTo('step2')}>{t('setupOverview.step2.link')}</button>
            </div>
            <div className="how-step-card">
              <div className="how-step-num n3">03</div>
              <h3>{t('setupOverview.step3.title')}</h3>
              <p>{t('setupOverview.step3.body')}</p>
              <button className="link" onClick={() => scrollTo('step3')}>{t('setupOverview.step3.link')}</button>
            </div>
          </div>
        </div>
      </section>

      {/* ============= STEP 1: CHECK YOUR PHONE ============= */}
      <section className="section how-deep" id="step1" style={{ background: 'var(--bg-sunk)' }}>
        <div className="section-inner">
          <div className="how-deep-head">
            <div className="how-deep-stamp n1">01</div>
            <div>
              <div className="eyebrow" style={{ marginBottom: 8 }}>{t('step1.eyebrow')}</div>
              <h2 className="h1">{t('step1.title')}</h2>
              <p className="lede" style={{ marginTop: 14 }}>
                {t('step1.ledePart1')}<em>{t('step1.ledeEmphasis1')}</em>{t('step1.ledePart2')}<em>{t('step1.ledeEmphasis2')}</em>{t('step1.ledePart3')}
              </p>
            </div>
          </div>

          {/* (A) Carrier-unlock check */}
          <div className="how-check-card">
            <div className="how-check-head">
              <span className="how-check-tag">A</span>
              <h3>{t('step1.checkA.title')}</h3>
            </div>
            <p>{t('step1.checkA.body')}</p>
            <div className="how-check-grid">
              <div className="how-check-platform">
                <div className="how-check-platform-h">
                  <Icon name="apple" size={14} /> {t('step1.checkA.iphone.heading')}
                </div>
                <ol>
                  <li>{t('step1.checkA.iphone.step1')}</li>
                  <li>{t('step1.checkA.iphone.step2')}</li>
                  <li>{t('step1.checkA.iphone.step3')}</li>
                </ol>
              </div>
              <div className="how-check-platform">
                <div className="how-check-platform-h">
                  <Icon name="android" size={15} /> {t('step1.checkA.android.heading')}
                </div>
                <ol>
                  <li>{t('step1.checkA.android.step1')}</li>
                  <li>{t('step1.checkA.android.step2')}</li>
                  <li>{t('step1.checkA.android.step3')}</li>
                </ol>
              </div>
            </div>
          </div>

          {/* (B) eSIM compatibility check */}
          <div className="how-check-card">
            <div className="how-check-head">
              <span className="how-check-tag">B</span>
              <h3>{t('step1.checkB.title')}</h3>
            </div>
            <p>{t('step1.checkB.body')}</p>

            <div className="how-check-grid">
              <div className="how-check-platform">
                <div className="how-check-platform-h">
                  <Icon name="apple" size={14} /> {t('step1.checkB.iphone.heading')}
                </div>
                <ol>
                  <li>{t('step1.checkB.iphone.step1')}</li>
                  <li>{t('step1.checkB.iphone.step2')}</li>
                  <li>{t('step1.checkB.iphone.step3')}</li>
                </ol>
              </div>
              <div className="how-check-platform">
                <div className="how-check-platform-h">
                  <Icon name="android" size={15} /> {t('step1.checkB.android.heading')}
                </div>
                <ol>
                  <li>{t('step1.checkB.android.step1')}</li>
                  <li>{t('step1.checkB.android.step2')}</li>
                  <li>{t('step1.checkB.android.step3')}</li>
                </ol>
              </div>
            </div>

            <div className="how-check-cta">
              <Link to={withLang('/compatible-devices', lang)} className="btn primary">
                {t('step1.cta')} <Icon name="arrow" size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============= STEP 2: PICK A PLAN ============= */}
      <section className="section how-deep" id="step2">
        <div className="section-inner">
          <div className="how-deep-head">
            <div className="how-deep-stamp n2">02</div>
            <div>
              <div className="eyebrow" style={{ marginBottom: 8 }}>{t('step2.eyebrow')}</div>
              <h2 className="h1">{t('step2.title')}</h2>
              <p className="lede" style={{ marginTop: 14 }}>{t('step2.lede')}</p>
            </div>
          </div>

          <div className="how-step2-snapshot">
            <div className="how-step2-row">
              <div className="how-step2-pill">
                <span className="how-step2-dot ok" />
                <div>
                  <strong>{t('step2.snap1Title')}</strong>
                  <div className="muted">{t('step2.snap1Sub')}</div>
                </div>
              </div>
              <div className="how-step2-pill">
                <span className="how-step2-dot pop" />
                <div>
                  <strong>{t('step2.snap2Title')}</strong>
                  <div className="muted">{t('step2.snap2Sub')}</div>
                </div>
              </div>
              <div className="how-step2-pill">
                <span className="how-step2-dot accent" />
                <div>
                  <strong>{t('step2.snap3Title')}</strong>
                  <div className="muted">{t('step2.snap3Sub')}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="how-plan-cta">
            <button type="button" className="btn primary lg" onClick={() => navigate(withLang('/destinations', lang))}>
              {t('step2.cta')} <Icon name="arrow" size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* ============= STEP 3: ACTIVATE ============= */}
      <section className="section how-deep" id="step3" style={{ background: 'var(--bg-sunk)' }}>
        <div className="section-inner">
          <div className="how-deep-head">
            <div className="how-deep-stamp n3">03</div>
            <div>
              <div className="eyebrow" style={{ marginBottom: 8 }}>{t('step3.eyebrow')}</div>
              <h2 className="h1">{t('step3.title')}</h2>
              <p className="lede" style={{ marginTop: 14 }}>
                {t('step3.ledePart1')}<em>{t('step3.ledeEmphasis')}</em>{t('step3.ledePart2')}
              </p>
            </div>
          </div>

          <div className="how-act-tabs">
            <button
              type="button"
              className={`how-act-tab ${activationMethod === 'onetap' ? 'active' : ''}`}
              onClick={() => setActivationMethod('onetap')}
            >
              <span className="dot" />
              <span>{t('step3.tabOnetap')}</span>
              <span className="badge">{t('step3.recommended')}</span>
            </button>
            <button
              type="button"
              className={`how-act-tab ${activationMethod === 'qr' ? 'active' : ''}`}
              onClick={() => setActivationMethod('qr')}
            >
              <span className="dot" />
              <span>{t('step3.tabQr')}</span>
            </button>
            <button
              type="button"
              className={`how-act-tab ${activationMethod === 'manual' ? 'active' : ''}`}
              onClick={() => setActivationMethod('manual')}
            >
              <span className="dot" />
              <span>{t('step3.tabManual')}</span>
            </button>
          </div>

          <p className="how-act-subtitle">{activeSubtitle}</p>

          <div className="how-act-grid">
            {activeCards.map((c, i) => (
              <div key={`${activationMethod}-${i}`} className="how-act-card">
                <div className="how-act-illust">
                  <ActivationIllust variant={c.illust} />
                </div>
                <div className="how-act-num mono">0{i + 1}</div>
                <h4>{c.title}</h4>
                <p>{c.body}</p>
              </div>
            ))}
          </div>

          <div className="how-act-tip">
            <span className="how-act-tip-icon">i</span>
            <div>
              <strong>{t('step3.tipTitle')}</strong>
              <span className="how-act-tip-body">{t('step3.tipBody')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============= FINAL CTA ============= */}
      <section className="section how-cta">
        <div className="section-inner">
          <div className="how-cta-card">
            <div className="how-cta-art">
              <CtaScene />
            </div>
            <div className="how-cta-body">
              <div className="eyebrow how-cta-eyebrow">{t('cta.eyebrow')}</div>
              <h2 className="h1 how-cta-h1">
                {t('cta.titleLine1')}
                <br />
                {t('cta.titlePart2')}<em>{t('cta.titleEmphasis')}</em>
              </h2>
              <p className="lede how-cta-lede">{t('cta.lede')}</p>
              <div className="how-cta-row">
                <button type="button" className="btn primary lg" onClick={() => navigate(withLang('/destinations', lang))}>
                  {t('cta.primary')} <Icon name="arrow" size={14} />
                </button>
                <button type="button" className="btn ghost lg" onClick={() => navigate(withLang('/', lang))}>
                  {t('cta.ghost')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO: contextual internal link to the blog article (English only —
          the piece is English). Gives the article another referring page. */}
      {lang === 'en' && (
        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--ink-3)', margin: '0 auto', padding: '0 24px 40px', maxWidth: 640 }}>
          Curious what separates a good travel eSIM from a cheap one?{' '}
          <a href="https://blog.nimvoy.com/blog/unpack-travel-esim-business/" style={{ color: '#B23A20' }}>
            Read our breakdown of network, speed and price →
          </a>
        </p>
      )}
    </>
  )
}

/* ============================================================
   SVG illustrations — lifted from the handoff pages_how.jsx.
   Palette-aware (use CSS vars), so they re-skin with the theme.
   ============================================================ */

function CtaScene() {
  return (
    <svg viewBox="0 0 280 240" className="how-cta-svg">
      <defs>
        <linearGradient id="ctaSky" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--accent-soft)" />
          <stop offset="100%" stopColor="var(--bg-sunk)" />
        </linearGradient>
      </defs>
      <rect width="280" height="240" rx="16" fill="url(#ctaSky)" />
      <circle cx="200" cy="80" r="32" fill="var(--pop)" opacity="0.85" />
      <circle cx="200" cy="80" r="48" fill="var(--pop)" opacity="0.15" />
      <path d="M 20 60 Q 120 30 270 50" stroke="var(--ink)" strokeWidth="1" strokeDasharray="3 4" fill="none" opacity="0.4" />
      <g transform="translate(250 45) rotate(-10)">
        <path d="M 0 0 L 16 -2 L 19 0 L 16 2 Z" fill="var(--ink)" />
      </g>
      <path d="M 0 180 L 60 140 L 100 165 L 140 130 L 180 158 L 230 135 L 280 162 L 280 240 L 0 240 Z" fill="var(--ink)" opacity="0.85" />
      <g transform="translate(40 130)">
        <rect width="50" height="90" rx="8" fill="var(--bg-elev)" stroke="var(--ink)" strokeWidth="1.5" />
        <rect x="6" y="10" width="38" height="60" rx="3" fill="var(--bg-sunk)" />
        <circle cx="25" cy="40" r="12" fill="var(--ok)" opacity="0.3" />
        <text x="25" y="44" textAnchor="middle" fontSize="9" fontFamily="var(--mono)" fill="var(--ok)">5G</text>
      </g>
    </svg>
  )
}

type ActivationIllustVariant =
  | 'envelope'
  | 'install-btn'
  | 'confirm'
  | 'laptop-qr'
  | 'settings'
  | 'scan'
  | 'email-keys'
  | 'paste'

function ActivationIllust({ variant }: { variant: ActivationIllustVariant }) {
  const phone = (children: React.ReactNode) => (
    <svg viewBox="0 0 160 220" className="how-act-svg">
      <rect x="20" y="10" width="120" height="200" rx="22" fill="none" stroke="var(--ink)" strokeWidth="2.2" />
      <rect x="64" y="14" width="32" height="6" rx="3" fill="var(--ink)" />
      {children}
    </svg>
  )

  switch (variant) {
    case 'envelope':
      return phone(
        <g>
          <rect x="46" y="60" width="68" height="50" rx="3" fill="none" stroke="var(--ink)" strokeWidth="2" />
          <path d="M 46 60 L 80 90 L 114 60" fill="none" stroke="var(--ink)" strokeWidth="2" />
          <rect x="46" y="140" width="68" height="22" rx="11" fill="var(--ink)" />
        </g>,
      )
    case 'install-btn':
      return phone(
        <g>
          <circle cx="80" cy="110" r="34" fill="none" stroke="var(--ink)" strokeWidth="2.2" />
          <rect x="48" y="98" width="64" height="24" rx="12" fill="var(--ink)" />
        </g>,
      )
    case 'confirm':
      return phone(
        <g>
          <g transform="translate(108 30)">
            <rect x="0" y="6" width="3" height="6" fill="var(--ink)" />
            <rect x="6" y="3" width="3" height="9" fill="var(--ink)" />
            <rect x="12" y="0" width="3" height="12" fill="var(--ink)" />
          </g>
          <circle cx="80" cy="110" r="26" fill="var(--accent-soft)" stroke="var(--ink)" strokeWidth="2" />
          <path d="M 70 110 L 78 118 L 92 102" fill="none" stroke="var(--ink)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        </g>,
      )
    case 'laptop-qr':
      return (
        <svg viewBox="0 0 200 160" className="how-act-svg">
          <rect x="30" y="20" width="140" height="90" rx="6" fill="none" stroke="var(--ink)" strokeWidth="2.2" />
          <rect x="20" y="110" width="160" height="14" rx="3" fill="none" stroke="var(--ink)" strokeWidth="2.2" />
          <rect x="90" y="110" width="20" height="4" rx="2" fill="var(--ink)" />
          <g transform="translate(74 36)">
            <rect width="52" height="52" fill="var(--bg)" />
            <rect x="0" y="0" width="14" height="14" fill="var(--ink)" />
            <rect x="3" y="3" width="8" height="8" fill="var(--bg)" />
            <rect x="5" y="5" width="4" height="4" fill="var(--ink)" />
            <rect x="38" y="0" width="14" height="14" fill="var(--ink)" />
            <rect x="41" y="3" width="8" height="8" fill="var(--bg)" />
            <rect x="43" y="5" width="4" height="4" fill="var(--ink)" />
            <rect x="0" y="38" width="14" height="14" fill="var(--ink)" />
            <rect x="3" y="41" width="8" height="8" fill="var(--bg)" />
            <rect x="5" y="43" width="4" height="4" fill="var(--ink)" />
            {Array.from({ length: 16 }).map((_, i) => {
              const x = 18 + (i % 4) * 5
              const y = 18 + Math.floor(i / 4) * 5
              return (i * 7) % 3 !== 0 ? <rect key={i} x={x} y={y} width="4" height="4" fill="var(--ink)" /> : null
            })}
          </g>
        </svg>
      )
    case 'settings':
      return phone(
        <g>
          <rect x="36" y="44" width="60" height="3" rx="1.5" fill="var(--ink)" />
          <g>
            <rect x="36" y="64" width="88" height="22" rx="4" fill="none" stroke="var(--ink)" strokeWidth="1.6" />
            <rect x="42" y="73" width="32" height="3" rx="1.5" fill="var(--ink)" />
            <path d="M 112 71 L 118 75 L 112 79" fill="none" stroke="var(--ink)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </g>
          <g>
            <rect x="36" y="94" width="88" height="22" rx="4" fill="var(--accent-soft)" stroke="var(--ink)" strokeWidth="1.6" />
            <rect x="42" y="103" width="40" height="3" rx="1.5" fill="var(--ink)" />
            <path d="M 112 101 L 118 105 L 112 109" fill="none" stroke="var(--ink)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </g>
          <g>
            <rect x="36" y="124" width="88" height="22" rx="4" fill="none" stroke="var(--ink)" strokeWidth="1.6" />
            <rect x="42" y="133" width="28" height="3" rx="1.5" fill="var(--ink)" />
          </g>
          <rect x="36" y="160" width="48" height="3" rx="1.5" fill="var(--ink)" />
        </g>,
      )
    case 'scan':
      return phone(
        <g>
          <path d="M 38 60 L 38 50 L 48 50" fill="none" stroke="var(--ink)" strokeWidth="2.4" strokeLinecap="round" />
          <path d="M 112 50 L 122 50 L 122 60" fill="none" stroke="var(--ink)" strokeWidth="2.4" strokeLinecap="round" />
          <path d="M 122 150 L 122 160 L 112 160" fill="none" stroke="var(--ink)" strokeWidth="2.4" strokeLinecap="round" />
          <path d="M 48 160 L 38 160 L 38 150" fill="none" stroke="var(--ink)" strokeWidth="2.4" strokeLinecap="round" />
          <g transform="translate(56 70)">
            <rect width="48" height="48" fill="var(--bg)" />
            <rect x="0" y="0" width="12" height="12" fill="var(--ink)" />
            <rect x="3" y="3" width="6" height="6" fill="var(--bg)" />
            <rect x="5" y="5" width="2" height="2" fill="var(--ink)" />
            <rect x="36" y="0" width="12" height="12" fill="var(--ink)" />
            <rect x="39" y="3" width="6" height="6" fill="var(--bg)" />
            <rect x="41" y="5" width="2" height="2" fill="var(--ink)" />
            <rect x="0" y="36" width="12" height="12" fill="var(--ink)" />
            <rect x="3" y="39" width="6" height="6" fill="var(--bg)" />
            <rect x="5" y="41" width="2" height="2" fill="var(--ink)" />
            {Array.from({ length: 16 }).map((_, i) => {
              const x = 16 + (i % 4) * 4
              const y = 16 + Math.floor(i / 4) * 4
              return (i * 7) % 3 !== 0 ? <rect key={i} x={x} y={y} width="3" height="3" fill="var(--ink)" /> : null
            })}
          </g>
        </g>,
      )
    case 'email-keys':
      return phone(
        <g>
          <rect x="46" y="48" width="68" height="46" rx="3" fill="none" stroke="var(--ink)" strokeWidth="2" />
          <path d="M 46 48 L 80 78 L 114 48" fill="none" stroke="var(--ink)" strokeWidth="2" />
          <rect x="46" y="110" width="68" height="20" rx="3" fill="none" stroke="var(--ink)" strokeWidth="1.6" strokeDasharray="3 3" />
          <rect x="52" y="117" width="36" height="6" fill="var(--ink)" opacity="0.5" />
          <rect x="46" y="138" width="68" height="20" rx="3" fill="none" stroke="var(--ink)" strokeWidth="1.6" strokeDasharray="3 3" />
          <rect x="52" y="145" width="44" height="6" fill="var(--ink)" opacity="0.5" />
        </g>,
      )
    case 'paste':
      return phone(
        <g>
          <rect x="36" y="60" width="88" height="26" rx="4" fill="none" stroke="var(--ink)" strokeWidth="1.8" strokeDasharray="3 3" />
          <rect x="42" y="70" width="44" height="6" fill="var(--ink)" opacity="0.5" />
          <rect x="36" y="96" width="88" height="26" rx="4" fill="none" stroke="var(--ink)" strokeWidth="1.8" strokeDasharray="3 3" />
          <rect x="42" y="106" width="56" height="6" fill="var(--ink)" opacity="0.5" />
          <rect x="50" y="142" width="60" height="22" rx="11" fill="var(--ink)" />
        </g>,
      )
    default:
      return phone(<g />)
  }
}
